import { useMemo, useState } from "react";
import {
  Camera, CalendarCheck, Wrench, MessageSquare, Receipt, Send, Lock, KeyRound,
  ArrowUpRight, ImageOff, EyeOff, Clock, CalendarClock,
} from "lucide-react";

const STAGE_LABEL = { move_in: "Move-in", during_tenancy: "During tenancy", move_out: "Move-out" };

// Existence-only (sensitive) event types: NEVER render content, amounts, names, addresses or thumbnails.
const SENSITIVE = new Set(["communication", "rent", "forwarding_notice"]);

const EVENT_META = {
  condition_photos: { icon: Camera, dot: "bg-brand", chip: "text-brand bg-brand/10 border-brand/20" },
  monthly_checkup: { icon: CalendarCheck, dot: "bg-navy", chip: "text-navy bg-navy/5 border-navy/10" },
  maintenance: { icon: Wrench, dot: "bg-amber-500", chip: "text-amber-700 bg-amber-50 border-amber-200" },
  communication: { icon: MessageSquare, dot: "bg-slate-400", chip: "text-[#475569] bg-slate-100 border-slate-200" },
  rent: { icon: Receipt, dot: "bg-slate-400", chip: "text-[#475569] bg-slate-100 border-slate-200" },
  forwarding_notice: { icon: Send, dot: "bg-slate-400", chip: "text-[#475569] bg-slate-100 border-slate-200" },
  inspection_request: { icon: CalendarClock, dot: "bg-navy", chip: "text-navy bg-navy/5 border-navy/10" },
  property_sealed: { icon: Lock, dot: "bg-emerald-500", chip: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  key_return_capsule: { icon: KeyRound, dot: "bg-emerald-500", chip: "text-emerald-700 bg-emerald-50 border-emerald-200" },
};

// Neutral, factual-only delivery status labels (never imply agreement or a response).
const DELIVERY_LABEL = { sent: "Sent", delivered: "Delivered", failed: "Delivery failed", bounced: "Bounced" };

function fmtDate(iso) {
  if (!iso) return "Undated";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function Thumb({ url, demo, alt }) {
  const [err, setErr] = useState(false);
  if (!url || err) return (
    <div className="grid place-items-center bg-slate-100 text-[#94A3B8] h-full w-full" data-testid="tl-thumb-error"><ImageOff size={14} /></div>
  );
  return (
    <div className="relative h-full w-full overflow-hidden">
      <img src={url} alt={alt} onError={() => setErr(true)} className="h-full w-full object-cover" />
      {demo && <span className="absolute bottom-0.5 right-0.5 text-[7px] font-bold tracking-widest text-white/90 bg-black/45 px-1 py-0.5 rounded">SAMPLE</span>}
    </div>
  );
}

function TimelineRow({ e, demo, onOpen, last }) {
  const meta = EVENT_META[e.event_type] || EVENT_META.monthly_checkup;
  const Icon = meta.icon;
  const sensitive = SENSITIVE.has(e.event_type);
  return (
    <li data-testid={`tl-event-${e.event_id}`} className="relative flex gap-3">
      {/* Rail */}
      <div className="flex flex-col items-center">
        <span className={`grid place-items-center h-8 w-8 rounded-full text-white shrink-0 ${meta.dot}`}><Icon size={15} /></span>
        {!last && <span className="w-px flex-1 bg-[#E2E8F0] mt-1" />}
      </div>
      {/* Body */}
      <div className="flex-1 min-w-0 pb-5">
        <div className="rounded-xl border border-[#E2E8F0] bg-white p-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-navy leading-snug" data-testid={`tl-label-${e.event_id}`}>{e.label}</p>
              <p className="mt-0.5 text-xs font-semibold text-[#334155]">{fmtDate(e.display_date)}</p>
            </div>
            {e.stage && (
              <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-navy/5 text-navy border border-navy/10">
                {STAGE_LABEL[e.stage] || e.stage}
              </span>
            )}
          </div>

          {/* Thumbnail only for condition photos (never for sensitive/existence-only events) */}
          {!sensitive && e.thumbnailUrl && (
            <div className="mt-2 h-16 w-24 rounded-lg border border-[#E2E8F0] overflow-hidden bg-slate-100" data-testid={`tl-thumb-${e.event_id}`}>
              <Thumb url={e.thumbnailUrl} demo={demo} alt={e.label} />
            </div>
          )}

          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <span data-testid={`tl-datesrc-${e.event_id}`}
              className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border ${meta.chip}`}>
              <Clock size={10} /> {e.date_source_label || "Recorded"}
            </span>
            {e.sealed === true && (
              <span data-testid={`tl-sealed-${e.event_id}`} className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">
                <Lock size={10} /> Sealed
              </span>
            )}
            {e.event_type === "inspection_request" && e.delivery_status && (
              <span data-testid={`tl-delivery-${e.event_id}`}
                className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border ${e.delivery_status === "failed" || e.delivery_status === "bounced" ? "text-amber-700 bg-amber-50 border-amber-200" : "text-emerald-700 bg-emerald-50 border-emerald-200"}`}>
                {DELIVERY_LABEL[e.delivery_status] || "Sent"}
              </span>
            )}
            {sensitive && (
              <span data-testid={`tl-private-${e.event_id}`} className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#64748B] bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5">
                <EyeOff size={10} /> Details kept private
              </span>
            )}
          </div>

          {onOpen && e.existing_route && (
            <button data-testid={`tl-open-${e.event_id}`} onClick={() => onOpen(e)}
              className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-brand hover:underline">
              Open <ArrowUpRight size={12} />
            </button>
          )}
        </div>
      </div>
    </li>
  );
}

// Shared, API-agnostic presentational layer. Reused by the real page and Demo Mode.
// Events must already be ordered oldest -> newest by the caller (backend does this).
export function TenancyTimelineView({ address, events = [], linkedCount = 0, demo = false, onOpenEvent }) {
  const [stage, setStage] = useState("all");
  const stages = ["all", "move_in", "during_tenancy", "move_out"];

  const visible = useMemo(
    () => events.filter((e) => stage === "all" || e.stage === stage),
    [events, stage],
  );

  return (
    <div data-testid="tenancy-timeline-view" className="space-y-4">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-wide text-[#94A3B8]">Tenancy Timeline</p>
        <h1 className="font-heading text-xl font-bold text-navy leading-tight">{address || "Your rental"}</h1>
        <p className="mt-1 text-xs text-[#64748B]">
          A chronological record of what you documented during your tenancy, oldest to newest.
          {linkedCount > 0 && " Combines your linked move-in and move-out records."} Communications, rent and forwarding notices appear as
          entries only — their contents stay private and are never shown here.
        </p>
      </div>

      {/* Lifecycle filter */}
      <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="tl-stage-filter">
        {stages.map((s) => (
          <button key={s} data-testid={`tl-stage-filter-${s}`} onClick={() => setStage(s)}
            className={`whitespace-nowrap text-[11px] font-semibold px-2.5 py-1.5 rounded-full border ${stage === s ? "bg-navy text-white border-navy" : "bg-white text-[#475569] border-[#E2E8F0]"}`}>
            {s === "all" ? "All" : STAGE_LABEL[s]}
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6 text-center text-sm text-[#64748B]" data-testid="tl-view-empty">
          No entries match this filter.
        </div>
      ) : (
        <ol data-testid="tl-stream" className="mt-1">
          {visible.map((e, i) => (
            <TimelineRow key={e.event_id} e={e} demo={demo} onOpen={onOpenEvent} last={i === visible.length - 1} />
          ))}
        </ol>
      )}
    </div>
  );
}
